import { useState } from "react";
import { api, apiError } from "../lib/api";
import { X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const newId = () => Math.random().toString(36).slice(2, 10);

export default function ScheduleEditSheet({ student, onClose, onSaved }) {
  const [blocks, setBlocks] = useState((student.profile.schedule || []).map((b) => ({ ...b })));
  const [saving, setSaving] = useState(false);

  const update = (i, k, v) => setBlocks((bs) => bs.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));
  const toggleDay = (i, day) => setBlocks((bs) => bs.map((b, idx) => {
    if (idx !== i) return b;
    const days = b.days.includes(day) ? b.days.filter((d) => d !== day) : [...b.days, day];
    return { ...b, days };
  }));
  const remove = (i) => setBlocks((bs) => bs.filter((_, idx) => idx !== i));
  const add = () => setBlocks((bs) => [...bs, { block_id: newId(), name: "", start: "09:00", end: "09:45", location: "", days: [...DAYS] }]);

  const save = async () => {
    if (blocks.some((b) => !b.name.trim())) { toast.error("Every block needs a name"); return; }
    setSaving(true);
    try {
      const sorted = [...blocks].sort((a, b) => a.start.localeCompare(b.start));
      await api.put(`/students/${student.id}/profile`, { schedule: sorted });
      toast.success("Schedule updated");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={onClose}>
      <div data-testid="schedule-edit-sheet" className="sf-sheet bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading font-semibold text-slate-900">Edit Daily Schedule</h2>
            <p className="text-xs text-slate-400">{student.first_name} {student.last_name} · blocks drive the aide's Today checklist</p>
          </div>
          <button data-testid="close-schedule-sheet-btn" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {blocks.map((b, i) => (
            <div key={b.block_id} data-testid={`schedule-block-row-${i}`} className="border border-slate-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <input data-testid={`block-name-input-${i}`} value={b.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Block name (e.g., Math)"
                  className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                <button data-testid={`remove-block-btn-${i}`} onClick={() => remove(i)} className="p-2 text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input data-testid={`block-start-input-${i}`} type="time" value={b.start} onChange={(e) => update(i, "start", e.target.value)}
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                <span className="text-slate-400 text-sm">→</span>
                <input data-testid={`block-end-input-${i}`} type="time" value={b.end} onChange={(e) => update(i, "end", e.target.value)}
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                <input data-testid={`block-location-input-${i}`} value={b.location} onChange={(e) => update(i, "location", e.target.value)} placeholder="Location"
                  className="flex-1 min-w-[120px] rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
              </div>
              <div className="flex gap-1.5">
                {DAYS.map((d) => (
                  <button key={d} type="button" data-testid={`block-day-${d.toLowerCase()}-${i}`} onClick={() => toggleDay(i, d)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors duration-150 ${
                      b.days.includes(d) ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                    }`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button data-testid="add-block-btn" onClick={add}
            className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-4 py-2.5 rounded-full transition-colors duration-150">
            <Plus size={15} /> Add block
          </button>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
          <button data-testid="save-schedule-btn" onClick={save} disabled={saving}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-full py-3 text-sm transition-colors duration-150 active:scale-[0.98] disabled:opacity-60">
            {saving ? "Saving…" : "Save schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
